import { z } from 'zod';

import {
  ChatMessageRecordSchema,
  UserMetadataSchema,
} from '../shared/schemas/ChatSchema.js';

export const AI_ASSISTANT_CHAT_SOCKET_PATH =
  '/api/ai-assistant/chats/ws' as const;

/** `connect` is absent: socket.io reserved events never arrive via `onAny`. */
export const CHAT_SOCKET_TOPICS = {
  authenticate: 'authenticate',
  message: 'message',
  error: 'error',

  voiceInputStart: 'voice_input_start',
  voiceInputCancel: 'voice_input_cancel',
  voiceInputChunk: 'voice_input_chunk',
  voiceInputMute: 'voice_input_mute',

  /** `session.updated` means the agent is initialised and ready for audio. */
  sessionUpdated: 'session.updated',
  responseCreated: 'response.created',

  userSpeechStarted: 'input_audio_buffer.speech_started',
  userSpeechStopped: 'input_audio_buffer.speech_stopped',
  agentAudioChunk: 'response.audio.delta',
  agentResponseDone: 'response.done',
} as const;

/**
 * Topics that skip the case transform: it rebuilds objects via `Object.keys`,
 * which destroys `Int16Array` and `ArrayBuffer`. Both carry one key, `audio`,
 * identical in snake and camel, so identity is the correct transform.
 */
export const BINARY_TOPICS = new Set<string>([
  CHAT_SOCKET_TOPICS.voiceInputChunk,
  CHAT_SOCKET_TOPICS.agentAudioChunk,
]);

export const ChatTargetSchema = z.object({
  isOverride: z.boolean(),
  context: z.number().nullable(),
  action: z.number().nullable(),
  feature: z.string().nullable(),
});

export type ChatTarget = z.infer<typeof ChatTargetSchema>;

export const AuthenticatePayloadSchema = z.object({
  authorization: z.string(),
});

export const OutgoingChatMessageSchema = z.object({
  chatTitle: z.string().nullish(),
  userPrompt: z.string(),
  metadata: UserMetadataSchema,
  target: ChatTargetSchema,
});

/**
 * Error branch of the incoming `message` union. The discriminator is the
 * PRESENCE of `detail`, never its type: the old code branched on
 * `'detail' in response` and never read the value. Typing it would reject a
 * `detail` sent as a dict or list (FastAPI's default) and the user would see
 * no error at all. An `unknown` key still requires presence, so the union
 * stays unambiguous. Nothing here reads `statusCode`.
 */
export const ChatSocketErrorSchema = z.object({
  statusCode: z.number().optional(),
  detail: z.unknown(),
});

export const ChatSocketMessageSchema = z.object({
  statusCode: z.number(),
  message: ChatMessageRecordSchema,
});

/** Error branch first: it is the safer resolution for a frame carrying both. */
export const IncomingChatMessageSchema = z.union([
  ChatSocketErrorSchema,
  ChatSocketMessageSchema,
]);

export type IncomingChatMessage = z.infer<typeof IncomingChatMessageSchema>;

/**
 * The standalone `error` topic. Only `statusCode` is read, so `detail` stays
 * `unknown`: rejecting a frame over its type would drop the 408 that triggers
 * re-auth, leaving the chat quietly dead.
 */
export const ChatSocketErrorEventSchema = z.object({
  statusCode: z.number(),
  detail: z.unknown().optional(),
});

export type ChatSocketErrorEvent = z.infer<typeof ChatSocketErrorEventSchema>;

export const VoiceChunkSchema = z.object({
  audio: z.custom<ArrayBuffer | ArrayBufferView>(
    (value) => value instanceof ArrayBuffer || ArrayBuffer.isView(value),
  ),
});

export type VoiceChunk = z.infer<typeof VoiceChunkSchema>;

export const AgentAudioChunkSchema = z.object({
  audio: z
    .custom<ArrayBuffer>(
      (value) => value instanceof ArrayBuffer || ArrayBuffer.isView(value),
    )
    .nullable(),
});

export type AgentAudioChunk = z.infer<typeof AgentAudioChunkSchema>;
